import { useEffect, useState } from 'react';
import { useGameStore } from '../../store/gameSlice';
import { useHubStore } from '../../store/hubSlice';
import { useCinematicStore } from '../../store/cinematicSlice';
import { hubService } from '../../services/HubService';
import { HUB_MAPS } from '../../data/hub-map.config';
import { KeyDisplay } from './KeyDisplay';
import './LocationPrompt.scss';

/**
 * Re-renders every frame via RAF to follow the player smoothly (the screen
 * position is derived from world coords, not from a store subscription).
 */
export const HubInteractionPrompt = () => {
  const gameplay = useGameStore((s) => s.gameplay);
  const activeInterface = useGameStore((s) => s.activeInterface);
  const screenWidth = useGameStore((s) => s.screenWidth);
  const screenHeight = useGameStore((s) => s.screenHeight);
  const zoomFactor = useGameStore((s) => s.zoomFactor);
  const mapLoaded = useHubStore((s) => s.mapLoaded);
  const currentHubMapId = useHubStore((s) => s.currentHubMapId);
  const cinematicActive = useCinematicStore((s) => s.active);
  const [, setTick] = useState(0);

  const active =
    gameplay === 'hub' && mapLoaded && !activeInterface && !cinematicActive;

  useEffect(() => {
    if (!active) {
      return;
    }
    let raf = 0;
    const loop = () => {
      setTick((t) => (t + 1) & 0xffff);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  if (!active) {
    return null;
  }

  const npc = hubService.getInteractableNpc(
    hubService.playerX,
    hubService.playerY,
  );
  if (!npc) {
    return null;
  }

  // Clamped camera — must stay in sync with the same formula in HubScene.
  const viewW = screenWidth / zoomFactor;
  const viewH = screenHeight / zoomFactor;
  const halfW = viewW / 2;
  const halfH = viewH / 2;
  const { width: mapW, height: mapH } = HUB_MAPS[currentHubMapId];
  const camX =
    mapW <= viewW ? mapW / 2 : clamp(hubService.playerX, halfW, mapW - halfW);
  const camY =
    mapH <= viewH ? mapH / 2 : clamp(hubService.playerY, halfH, mapH - halfH);

  // Anchor the prompt above the point. `promptOffset` lets each invisible POI
  // raise its call-to-action (no sprite height to anchor against anymore).
  const screenX = screenWidth / 2 + (npc.x - camX) * zoomFactor;
  const screenY =
    screenHeight / 2 +
    (npc.y - camY - (npc.promptOffset ?? npc.spriteHeight ?? 0)) * zoomFactor;

  return (
    <div
      className="location-prompt"
      style={{
        left: screenX,
        top: screenY,
        transform: 'translate(-50%, -100%)',
      }}
    >
      <div className="location-prompt-row">
        <KeyDisplay context="dungeon" action="INTERACT" size="md" />
        <span className="location-prompt-label">Interagir</span>
      </div>
    </div>
  );
};

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
